import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { cardFaceSvg, cardFaceDataUri } from '@/lib/cardFace';
import { encodeQr } from '@/lib/qr';
import type { VerificationCredential } from '@/types/verification';
import { formatDate } from '@/utils/format';

interface CredentialCardDialogProps {
  open: boolean;
  onClose: () => void;
  credential: VerificationCredential | null;
  /** The holder's name as the console knows it; em dash when it does not. */
  personName: string;
  institutionName: string;
}

/**
 * The printable card face for one credential.
 *
 * The SVG is produced by the pure `cardFaceSvg` builder and shown through an
 * <img> data URI, so the preview and the print output share one artifact. The
 * QR is a real scannable code rendered by the in-repo encoder (src/lib/qr.ts)
 * and encodes ONLY the opaque `crd_…` reference — never a name or any PII.
 *
 * Print opens a bare window holding just the SVG rather than window.print() on
 * the app: the console shell (rails, toasts, dialogs) must never end up on a
 * plastic card, and a dedicated document is the one reliable way to keep it off.
 */
export function CredentialCardDialog({
  open,
  onClose,
  credential,
  personName,
  institutionName,
}: CredentialCardDialogProps) {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language || 'en').split('-')[0];

  const faceInput = useMemo(() => {
    if (!credential) return null;
    return {
      displayName: personName || '—',
      institutionName,
      credentialPublicId: credential.credentialPublicId,
      credentialTypeLabel: t(`credentials.type.${credential.credentialType}`, {
        defaultValue: credential.credentialType,
      }),
      expiryLabel: credential.expiresAt
        ? t('credentials.card.expires', { date: formatDate(credential.expiresAt, locale) })
        : '',
      noQrNote: t('credentials.card.noQr'),
    };
  }, [credential, personName, institutionName, t, locale]);

  if (!credential || !faceInput) return null;

  const qrRendered = encodeQr(credential.credentialPublicId) !== null;

  const print = () => {
    const svg = cardFaceSvg(faceInput);
    const w = window.open('', '_blank', 'noopener,width=480,height=360');
    if (!w) return;
    w.document.write(
      `<!doctype html><title>${t('credentials.card.title')}</title>` +
        `<body style="margin:24px;display:flex;justify-content:center">${svg}</body>`,
    );
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <Modal open={open} onClose={onClose} title={t('credentials.card.title')}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <img
          src={cardFaceDataUri(faceInput)}
          alt={t('credentials.card.alt', { name: faceInput.displayName })}
          style={{ width: '100%', maxWidth: '22rem', alignSelf: 'center' }}
        />
        <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
          {qrRendered ? t('credentials.card.qrNote') : t('credentials.card.noQr')}
        </p>
        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={onClose}>
            {t('common.close')}
          </button>
          <button type="button" className="modal-button" onClick={print}>
            {t('credentials.card.print')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
